import React, { useEffect, useState } from 'react';
import FilterGroup from 'components/ListGroup';
import PageHeader from 'components/PageHeader';
import Pagination from 'components/Pagination';
import { SortColumn, SortOrder } from 'components/Table/Table.types';
import { useProjectsPageData } from 'hooks/useProjectsPageData';
import ErrorLayout from 'layouts/ErrorLayout';
import LoadingLayout from 'layouts/LoadingLayout';
import ProjectModal from 'pages/Projects/ProjectModal';
import { IFilter, IProject } from 'pages/Projects/Projects.types';
import ProjectsTable from 'pages/Projects/ProjectsTable';
import { useTranslation } from 'react-i18next';
import { paginate } from 'utils/paginate';
import { filterProjects, findProjectById } from 'utils/projects';
import { sort } from 'utils/sort';

import { Container, Paper, TableContainer, Typography } from '@mui/material';
import { Box } from '@mui/system';

const ITEMS_PER_PAGE = 4;
const STARTING_PAGE_NUMBER = 1;
const DEFAULT_FILTER = {
  id: 'allProjects',
  name: 'All',
  default: true,
};
const DEFAULT_SORT_COLUMN = { path: '', order: SortOrder.ASC };

export default function Projects() {
  const { t } = useTranslation();
  const { data, error, loading } = useProjectsPageData();
  const projects = data?.projects ?? [];
  const filters = data?.filters ?? [];

  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [previewProject, setPreviewProject] = useState<IProject>();
  const [currentPage, setCurrentPage] = useState<number>(STARTING_PAGE_NUMBER);
  const [selectedFilter, setSelectedFilter] = useState<IFilter>(DEFAULT_FILTER);
  const [sortColumn, setSortColumn] = useState<SortColumn>(DEFAULT_SORT_COLUMN);

  useEffect(() => {
    if (data) {
      setSortColumn({ path: data.projects[0].title, order: SortOrder.ASC });
    }
  }, []);

  const handlePreview = (id: string) => {
    const project = findProjectById(projects, id);
    setIsPreview(true);
    setPreviewProject(project);
  };
  const handleClose = () => {
    setIsPreview(false);
  };
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  const handleFilterSelect = (item: IFilter) => {
    setSelectedFilter(item);
    setCurrentPage(STARTING_PAGE_NUMBER);
  };
  const handleSort = (sortColumn: SortColumn) => {
    setSortColumn(sortColumn);
  };

  const getPagedData = () => {
    const filtered = filterProjects(projects, selectedFilter);
    const sorted = sort(filtered, sortColumn.path, sortColumn.order);
    const pagedProjects = paginate(sorted, currentPage, ITEMS_PER_PAGE);

    return { totalCount: filtered.length, data: pagedProjects };
  };
  const { totalCount, data: pagedProjects } = getPagedData();

  return (
    <Box sx={{ pb: 12 }}>
      <PageHeader>Projects</PageHeader>
      <LoadingLayout loading={loading}>
        <ErrorLayout data={data} error={error}>
          {projects.length === 0 ? (
            <Container>
              <Typography sx={{ textAlign: 'center' }} variant='subtitle2'>
                {t('projects.fetchError')}
              </Typography>
            </Container>
          ) : (
            <Container>
              {previewProject && (
                <ProjectModal
                  open={isPreview}
                  onClose={handleClose}
                  project={previewProject}
                />
              )}
              <TableContainer component={Paper}>
                <Typography variant='subtitle2' sx={{ m: 2 }}>
                  {t('projects.subtitle1')} {totalCount}{' '}
                  {t('projects.subtitle2')}
                </Typography>
                <FilterGroup
                  items={filters}
                  selectedItem={selectedFilter}
                  onItemSelect={handleFilterSelect}
                />
                <ProjectsTable
                  projects={pagedProjects}
                  sortColumn={sortColumn}
                  onPreview={handlePreview}
                  onSort={handleSort}
                />
                <Pagination
                  itemsCount={totalCount}
                  pageSize={ITEMS_PER_PAGE}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </TableContainer>
            </Container>
          )}
        </ErrorLayout>
      </LoadingLayout>
    </Box>
  );
}
